import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { IMG } from "@/lib/api";
import { scrollToId } from "./SmoothScroll";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
  <>Spotless.</>,
  <>Shameless.</>,
  <>Clean as <span className="font-serif-i normal-case text-acid">fuck.</span></>,
];

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.04em]">
    <motion.span className="block" initial={{ y: "105%" }} animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.15 + i * 0.14, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const TiltFrame = ({ y }) => {
  const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
  const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 14);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 14);
  };
  const reset = () => { rx.set(0); ry.set(0); };
  return (
    <motion.div style={{ y }} className="[perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div style={{ rotateX: rx, rotateY: ry }}
        initial={{ clipPath: "inset(100% 0 0 0)", scale: 1.08 }}
        animate={{ clipPath: "inset(0% 0 0 0)", scale: 1 }}
        transition={{ duration: 1.4, delay: 0.45, ease: EASE }}
        className="clip-notch relative aspect-[4/5] w-full overflow-hidden bg-zinc-900 shadow-2xl" data-testid="hero-image-frame">
        <img src={IMG.hero} alt="Immaculate luxury living room" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between font-mono-x text-[11px] uppercase tracking-[0.2em] text-zinc-200">
          <span>Spec. 001<br />Living room, after</span>
          <span className="text-acid">● Spotless</span>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--hx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--hy", `${e.clientY - r.top}px`);
  };
  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="grain relative min-h-[100svh] overflow-hidden pt-28 sm:pt-32"
      style={{ background: "radial-gradient(600px circle at var(--hx,70%) var(--hy,30%), rgba(237,184,158,0.09), transparent 60%), #090A0C" }}>
      <div className="relative mx-auto grid max-w-[1400px] gap-10 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-8">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }} className="eyebrow mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-[var(--acid)]" /> Residential & Commercial Cleaning
          </motion.p>
          <h1 className="font-display text-[17vw] uppercase leading-[0.88] text-zinc-50 sm:text-[13vw] lg:text-[9.2vw] xl:text-[8.6rem]" data-testid="hero-heading">
            {LINES.map((l, i) => <MaskLine key={i} i={i}>{l}</MaskLine>)}
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
            className="mt-10 flex max-w-xl flex-col gap-8">
            <p className="text-base leading-relaxed text-zinc-300 sm:text-lg">
              Luxury-grade cleaning for homes and businesses, delivered with zero fluff and a little attitude.
              We don&apos;t do &ldquo;good enough.&rdquo; We do <span className="font-serif-i text-xl text-zinc-50">obscenely clean.</span>
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/book" data-testid="hero-book-button"
                className="group inline-flex items-center gap-3 rounded-full bg-acid px-7 py-4 text-base font-bold text-black transition-transform duration-300 hover:scale-[1.04]">
                Book your clean <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45" />
              </Link>
              <button onClick={() => scrollToId("services")} data-testid="hero-explore-button"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-4 text-sm font-semibold text-zinc-100 transition-colors duration-300 hover:border-[var(--acid)] hover:text-[var(--acid)]">
                See services <ArrowDown className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        </motion.div>
        <div className="relative lg:col-span-4 lg:pt-24">
          <div className="absolute -left-12 -top-10 z-20 hidden h-36 w-36 items-center justify-center lg:flex">
            <svg viewBox="0 0 100 100" className="spin-slow absolute inset-0" style={{ overflow: "visible" }}>
              <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
              <text fill="#e4e4e7" fontSize="9.5" letterSpacing="1.6" fontFamily="JetBrains Mono">
                <textPath href="#circ">NO DUST LEFT ALIVE • SPOTLESS • </textPath>
              </text>
            </svg>
            <img src="/images/logo-sm.png" alt="" className="h-20 w-20 rounded-full shadow-[0_0_40px_rgba(0,0,0,0.6)]" data-testid="hero-logo-badge" />
          </div>
          <TiltFrame y={imgY} />
        </div>
      </div>
    </section>
  );
};
